/**
 * Which fields an Edit Event save must clear.
 *
 * UpdateEvent is a PATCH: an omitted field means "unchanged", so emptying a box and saving used to
 * leave the stored value in place (Event Hub Testing doc #38). The service now takes
 * `clearFields` — the scalar fields to set back to unset (AWSECEventHubModel ClearableEventField).
 * List fields are cleared differently, by sending `[]`, and are not handled here.
 *
 * A field is cleared when the form EDITS it, the patch leaves it out (the box is empty), and the
 * stored event has a value — clearing an already-empty field is harmless but noisy in the audit.
 * "Edits" matters: the nomination window is hidden while Nomination Required is No and the overall
 * target while By BU is showing, and a hidden field is left as stored, not cleared.
 */
import type { ClearableEventField } from '@amzn/awseceventhub-client';
import type { Event, UpdateEventInput } from '../client/EventCalendarClient';

/** The patch fields the form can clear, and the service's name for each. */
export const CLEARABLE = {
  sfdcCampaignId: 'SFDC_CAMPAIGN_ID',
  eventType: 'EVENT_TYPE',
  eventSubType: 'EVENT_SUB_TYPE',
  province: 'PROVINCE',
  city: 'CITY',
  startDate: 'START_DATE',
  endDate: 'END_DATE',
  nominationStartDate: 'NOMINATION_START_DATE',
  nominationEndDate: 'NOMINATION_END_DATE',
  overallTarget: 'OVERALL_TARGET',
  description: 'DESCRIPTION',
} as const satisfies Record<string, ClearableEventField>;

export type ClearableKey = keyof typeof CLEARABLE;
export type ClearableField = (typeof CLEARABLE)[ClearableKey];

function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (typeof v === 'object' && !(v instanceof Date)) {
    return Object.values(v as Record<string, unknown>).some((x) => x !== undefined && x !== null);
  }
  return true;
}

/**
 * The fields to name in `clearFields`: of those the form edits (`edited`), the ones the patch
 * leaves out while the stored event has a value.
 */
export function fieldsToClear(
  stored: Event | undefined,
  patch: Partial<UpdateEventInput>,
  edited: readonly ClearableKey[],
): ClearableField[] {
  if (!stored) return [];
  return edited
    .filter((k) => patch[k] === undefined && hasValue(stored[k]))
    .map((k) => CLEARABLE[k]);
}

/**
 * `[]` for a list the form emptied when the stored event had entries, so the service replaces it
 * with nothing; otherwise what the form holds (undefined for an empty list nobody had set).
 */
export function listForSave<T>(list: readonly T[], stored: readonly T[] | undefined): T[] | undefined {
  if (list.length) return [...list];
  return stored?.length ? [] : undefined;
}
